// Skills Management API (Epic #281).
// Field names are snake_case — the SM backend schemas serialize verbatim.
import client from './client';

// Skill classification. Skills Management runs on 'technical' skills only:
// #355 retired 'core_competency' from every surface a user can see. The
// union keeps both values because the wire format is unchanged — the field
// still arrives on retained rows (legacy capability requirements, history)
// and the backend schema still admits it.
export type SkillType = 'core_competency' | 'technical';

// --- Catalogue ---------------------------------------------------------------

export interface SmSkill {
  id: string;
  name: string;
  skill_type: SkillType;
  description?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SmRubricLevel {
  id: string;
  skill_type: SkillType;
  value: number;
  label: string;
  description?: string | null;
}

// #357 — the catalogue outgrew a single page. `PaginationParams` caps
// `page_size` at 100 and this used to ask for exactly one page, so the
// 101st skill onwards (alphabetically, everything after "Solution
// Architecture") silently never arrived. Every consumer of this call — the
// catalogue page, the request dialog's picker, the merge-target list — needs
// the whole set, so walk the pages until `total` is reached.
export const getSkills = async (includeInactive = false): Promise<SmSkill[]> => {
  const pageSize = 100;
  const items: SmSkill[] = [];
  let page = 1;
  let total = 0;
  do {
    const response = await client.get<{ items: SmSkill[]; total: number }>('/sm/skills', {
      params: { page, page_size: pageSize, include_inactive: includeInactive },
    });
    items.push(...response.data.items);
    total = response.data.total;
    // A short page is the end whatever `total` says — never spin on a
    // server that disagrees with itself.
    if (response.data.items.length < pageSize) break;
    page += 1;
  } while (items.length < total);
  return items;
};

// #355 — only 'technical' is accepted; the API 422s anything else.
export const createSkill = async (data: {
  name: string;
  skill_type: 'technical';
  description?: string;
}): Promise<SmSkill> => {
  const response = await client.post<SmSkill>('/sm/skills', data);
  return response.data;
};

export const updateSkill = async (
  skillId: string,
  data: { name?: string; description?: string; is_active?: boolean }
): Promise<SmSkill> => {
  const response = await client.patch<SmSkill>(`/sm/skills/${skillId}`, data);
  return response.data;
};

export const mergeSkill = async (
  sourceSkillId: string,
  targetSkillId: string
): Promise<Record<string, unknown>> => {
  const response = await client.post(`/sm/skills/${sourceSkillId}/merge`, {
    target_skill_id: targetSkillId,
  });
  return response.data;
};

export const getRubric = async (): Promise<SmRubricLevel[]> => {
  const response = await client.get<SmRubricLevel[]>('/sm/rubric');
  return response.data;
};

export const updateRubricLevel = async (
  skillType: SkillType,
  value: number,
  description: string
): Promise<SmRubricLevel> => {
  const response = await client.patch<SmRubricLevel>(
    `/sm/rubric/${skillType}/${value}`,
    { description }
  );
  return response.data;
};

// --- Assessments -------------------------------------------------------------

export interface SmFormSkill {
  skill_id: string;
  name: string;
  skill_type: SkillType;
  description?: string | null;
  current_rating?: number | null;
  last_assessed_at?: string | null;
}

export interface SmAssessmentForm {
  subject_user_id: string;
  subject_display_name: string;
  /**
   * #355 — always empty. Core competencies are retired from assessment;
   * the field stays on the wire so a stale cached bundle still reads a
   * list. Nothing in this client should read it.
   */
  core_competencies: SmFormSkill[];
  /**
   * #346 — with a request present, the skills it asks about. Empty on an
   * open-ended form. (Before #355 this travelled in `core_competencies`.)
   */
  scoped_skills: SmFormSkill[];
  technical_on_profile: SmFormSkill[];
  technical_available: SmFormSkill[];
  /** The technical rubric only, since #355. */
  rubric: SmRubricLevel[];
  // Feature #325/#350 — the SIGNED-IN USER's open draft for this subject, if
  // any. Scoped server-side to the caller as assessor, so a subject loading
  // their own form never sees a manager's in-progress peer draft.
  draft?: SmDraft | null;
  /**
   * Feature #326 — the request being answered, when the form was opened
   * against one. With a request present, `scoped_skills` holds the
   * requested skills and both technical lists are empty: R6's "a peer may
   * introduce a skill" does not extend to an ask that named its scope.
   */
  request?: SmAssessmentRequest | null;
  /**
   * Feature #345 — false when the payload deliberately carries no
   * `current_rating` anywhere. Enforced server-side; the flag exists so
   * the form can SAY so, rather than silently rendering every skill as
   * unrated, which reads like a bug.
   */
  show_current_ratings: boolean;
  /** Feature #347 — whether this assessor may decline a skill. False on a
   *  self-assessment: you are always in a position to judge yourself. */
  allow_opt_out: boolean;
}

export interface SmSubmissionItem {
  skill_id: string;
  rating: number;
  comment?: string;
}

/**
 * Feature #347 — a skill the assessor declined to rate. Deliberately a
 * separate list from `items` rather than an item with a null rating:
 * "I cannot judge this" is not a rating, and the two must not share a
 * shape that lets one be mistaken for the other.
 */
export interface SmOptOutInput {
  skill_id: string;
  reason?: string | null;
}

export interface SmSubmissionResult {
  submission_id: string;
  skills_rated: number;
  /** Feature #347 — how many skills the assessor could not judge. */
  skills_declined: number;
  deltas_opened: number;
  request_id?: string | null;
  /**
   * Feature #326/#343 — this answered a request, so it moved no rating
   * directly. Every proposal went to a delta for a manager to settle,
   * even on a skill that had no rating at all.
   */
  advisory: boolean;
}

/**
 * Pass `requestId` to answer a specific assessment request. The form then
 * comes back scoped to the skills asked about (#346) and, if the request
 * says so, with no current ratings anywhere in the payload (#345).
 */
export const getAssessmentForm = async (
  userId: string,
  requestId?: string | null
): Promise<SmAssessmentForm> => {
  const response = await client.get<SmAssessmentForm>(
    `/sm/assessments/form/${userId}`,
    { params: requestId ? { request_id: requestId } : undefined }
  );
  return response.data;
};

export const submitAssessment = async (data: {
  subject_user_id: string;
  assessment_type: 'self' | 'peer';
  items: SmSubmissionItem[];
  opt_outs?: SmOptOutInput[];
  request_id?: string | null;
}): Promise<SmSubmissionResult> => {
  const response = await client.post<SmSubmissionResult>('/sm/assessments', data);
  return response.data;
};

// --- Assessment requests (Feature #326; #343 grant, #345 blind, #346 scope) ---

export interface SmAssessmentRequestSkill {
  skill_id: string;
  skill_name: string;
  skill_type: SkillType;
}

export interface SmAssessmentRequest {
  id: string;
  subject_user_id: string;
  subject_display_name?: string | null;
  assessor_user_id: string;
  assessor_display_name?: string | null;
  requested_by_user_id: string;
  requested_by_display_name?: string | null;
  requested_at: string;
  due_at?: string | null;
  status: 'pending' | 'submitted' | 'revoked';
  /** #345 — false means the assessor works blind. */
  show_current_ratings: boolean;
  note?: string | null;
  skills: SmAssessmentRequestSkill[];
  submission_id?: string | null;
  submitted_at?: string | null;
  revoked_at?: string | null;
  assess_url: string;
}

/** 409 body — an open request for this pair already exists. */
export interface SmAssessmentRequestConflict {
  detail: string;
  request: SmAssessmentRequest;
}

export const createAssessmentRequest = async (data: {
  subject_user_id: string;
  assessor_user_id: string;
  skill_ids: string[];
  show_current_ratings?: boolean;
  due_at?: string | null;
  note?: string | null;
}): Promise<SmAssessmentRequest> => {
  const response = await client.post<SmAssessmentRequest>(
    '/sm/assessments/requests',
    data
  );
  return response.data;
};

/** Requests ABOUT one person. sm_manager only — see `getMyAssessments` for
 *  what has been asked OF you, which every user can read. */
export const getAssessmentRequests = async (
  subjectUserId: string,
  status?: 'pending' | 'submitted' | 'revoked'
): Promise<SmAssessmentRequest[]> => {
  const response = await client.get<{ items: SmAssessmentRequest[]; total: number }>(
    '/sm/assessments/requests',
    { params: { subject_user_id: subjectUserId, status } }
  );
  return response.data.items;
};

export const revokeAssessmentRequest = async (
  requestId: string
): Promise<SmAssessmentRequest> => {
  const response = await client.post<SmAssessmentRequest>(
    `/sm/assessments/requests/${requestId}/revoke`,
    {}
  );
  return response.data;
};

// --- Draft assessments (Feature #325; #348 reconciliation, #350 uniqueness) ---

// One deliberate answer on a draft. `rating` is optional: a skill can sit on
// a draft unanswered (added from the technical picker, or commented on before
// being rated). Skills the assessor never touched are NOT sent — the form
// pre-populates every row with the subject's current rating, so persisting
// untouched rows would make "agree with current" (R2) indistinguishable from
// "not looked at yet".
export interface SmDraftItemInput {
  skill_id: string;
  rating?: number | null;
  comment?: string | null;
  // #348 — the assessor has read the stale-baseline warning and stands by
  // this answer. Re-anchors the stored baseline; a plain save does not.
  reconfirm?: boolean;
  // #347 — "I cannot judge this". A deliberate answer, distinct both from
  // an absent rating (not looked at yet) and from submitting the current
  // value (which reads as agreement, and should).
  opted_out?: boolean;
  opt_out_reason?: string | null;
}

// One saved answer measured against the subject's live rating.
export interface SmDraftReconciledItem {
  skill_id: string;
  skill_name: string;
  skill_type: string;
  rating?: number | null;
  comment?: string | null;
  baseline_rating?: number | null;
  current_rating?: number | null;
  // 'assessment' or 'review' — what moved the rating, where known.
  changed_by?: string | null;
  baseline_changed: boolean;
  needs_reconfirmation: boolean;
  skill_unavailable: boolean;
  // #347 — carried across a save/resume like any other answer.
  opted_out: boolean;
  opt_out_reason?: string | null;
}

export interface SmDraft {
  id: string;
  subject_user_id: string;
  subject_display_name?: string | null;
  assessment_type: 'self' | 'peer';
  request_id?: string | null;
  created_at: string;
  updated_at: string;
  items: SmDraftReconciledItem[];
  changed_skill_ids: string[];
  reconfirm_skill_ids: string[];
  unavailable_skill_ids: string[];
}

// 409 body from createDraft — everything needed to offer Resume (#350).
export interface SmDraftConflict {
  detail: string;
  draft_id: string;
  subject_user_id: string;
  subject_display_name?: string | null;
  assessment_type: 'self' | 'peer';
  updated_at: string;
  resume_url: string;
}

// One row of My Assessments. Deliberately heterogeneous with a `kind`
// discriminator so #326's requested peer assessments — and later, submitted
// history — join this list without a second contract.
export interface SmMyAssessmentRow {
  kind: 'draft' | 'peer_request' | 'submitted';
  draft_id?: string | null;
  subject_user_id: string;
  subject_display_name: string;
  assessment_type: 'self' | 'peer';
  request_id?: string | null;
  requested_by_user_id?: string | null;
  requested_by_display_name?: string | null;
  requested_at?: string | null;
  due_at?: string | null;
  status: string;
  skills_answered: number;
  /** #346 — what a requested assessment covers, so the row says what is
   *  being asked and not only who asked. */
  scope_skill_names: string[];
  /** #345 — surfaced here so an assessor knows before opening it whether
   *  they are being asked for an independent view. */
  show_current_ratings: boolean;
  note?: string | null;
  created_at: string;
  updated_at: string;
  resume_url: string;
}

export const getMyAssessments = async (): Promise<SmMyAssessmentRow[]> => {
  const response = await client.get<{ items: SmMyAssessmentRow[]; total: number }>(
    '/sm/assessments/mine'
  );
  return response.data.items;
};

export const createDraft = async (data: {
  subject_user_id: string;
  assessment_type: 'self' | 'peer';
  request_id?: string | null;
  items: SmDraftItemInput[];
}): Promise<SmDraft> => {
  const response = await client.post<SmDraft>('/sm/assessments/drafts', data);
  return response.data;
};

export const saveDraft = async (
  draftId: string,
  items: SmDraftItemInput[]
): Promise<SmDraft> => {
  const response = await client.put<SmDraft>(`/sm/assessments/drafts/${draftId}`, {
    items,
  });
  return response.data;
};

export const discardDraft = async (draftId: string): Promise<void> => {
  await client.delete(`/sm/assessments/drafts/${draftId}`);
};

// Submits the draft and consumes it. The body is the final DRAFT state, not a
// submission: the server saves it first, which is what lets #348's
// stale-baseline block be enforced there rather than trusted to this client.
// Untouched skills are filled in server-side from the live current rating.
export const submitDraft = async (
  draftId: string,
  items: SmDraftItemInput[]
): Promise<SmSubmissionResult> => {
  const response = await client.post<SmSubmissionResult>(
    `/sm/assessments/drafts/${draftId}/submit`,
    { items }
  );
  return response.data;
};

// --- Deltas & reviews ----------------------------------------------------------

export interface SmDelta {
  id: string;
  user_id: string;
  user_display_name?: string | null;
  skill_id: string;
  skill_name?: string | null;
  skill_type?: SkillType | null;
  previous_rating?: number | null;
  proposed_rating: number;
  proposed_by_user_id: string;
  proposed_by_display_name?: string | null;
  proposed_at: string;
  /** #351/#349 — the proposer's reason, off the assessment that raised
   *  this delta. Mandatory on peer ratings that change or challenge a
   *  skill since #351; older deltas and self proposals may carry none. */
  comment?: string | null;
  review_id?: string | null;
  state: 'open' | 'in_review' | 'resolved';
  agreed_rating?: number | null;
  delta_notes?: string | null;
  resolved_at?: string | null;
}

/**
 * Feature #340 — every competing proposal on one skill, as one thing to
 * decide. A skill routinely carries three or four now: the subject's own
 * rating, a requested colleague's view, the manager's own. They are
 * settled together by one agreed number, so they are presented together.
 */
export interface SmReviewSkillGroup {
  skill_id: string;
  skill_name?: string | null;
  skill_type?: SkillType | null;
  /** The live rating on the profile — read from `sm_person_skill`, not
   *  from any one delta's `previous_rating`, which is only the baseline as
   *  it stood when THAT delta was raised. */
  current_rating?: number | null;
  /** Oldest first — the order the opinions arrived in. */
  deltas: SmDelta[];
  agreed_rating?: number | null;
  delta_notes?: string | null;
  resolved: boolean;
}

export interface SmReview {
  id: string;
  user_id: string;
  user_display_name?: string | null;
  opened_at: string;
  opened_by: string;
  status: 'in_review' | 'resolved';
  resolved_at?: string | null;
  meeting_notes?: string | null;
  deltas: SmDelta[];
  /** Feature #340 — the same deltas as the decisions actually being made.
   *  `deltas` is retained so nothing reading it breaks; render this. */
  skill_groups: SmReviewSkillGroup[];
  /** True when the caller is the subject rather than an sm_manager. They
   *  may READ the review — #340 has both parties working through the same
   *  screen — but only a manager records or closes anything. */
  read_only: boolean;
}

export interface SmReviewsPending {
  open_deltas: SmDelta[];
  reviews_in_progress: SmReview[];
}

/**
 * Feature #344 — one unfinished assessment that could still raise a delta.
 *
 * Draft rows carry the holder and the dates and nothing else. A draft is
 * an unpublished opinion about a named person; the manager deciding
 * whether to wait needs to know it exists and who has it, and has no
 * business seeing what is in it.
 */
export interface SmOutstandingAssessment {
  kind: 'draft' | 'request';
  id: string;
  assessment_type: 'self' | 'peer';
  holder_user_id: string;
  holder_display_name?: string | null;
  requested_by_display_name?: string | null;
  occurred_at: string;
  updated_at: string;
  due_at?: string | null;
  skill_names: string[];
}

export interface SmOutstanding {
  subject_user_id: string;
  subject_display_name?: string | null;
  items: SmOutstandingAssessment[];
  total: number;
}

/**
 * What is still in flight for this person. A WARNING feed, never a gate —
 * `createReview` is deliberately unchanged and refuses nothing.
 */
export const getOutstandingAssessments = async (
  userId: string
): Promise<SmOutstanding> => {
  const response = await client.get<SmOutstanding>('/sm/reviews/outstanding', {
    params: { user_id: userId },
  });
  return response.data;
};

export const getDeltas = async (params?: {
  user_id?: string;
  state?: string;
}): Promise<SmDelta[]> => {
  const response = await client.get<SmDelta[]>('/sm/deltas', { params });
  return response.data;
};

export const getReviewsPending = async (): Promise<SmReviewsPending> => {
  const response = await client.get<SmReviewsPending>('/sm/reviews/pending');
  return response.data;
};

export const createReview = async (
  subjectUserId: string,
  deltaIds: string[]
): Promise<SmReview> => {
  const response = await client.post<SmReview>('/sm/reviews', {
    subject_user_id: subjectUserId,
    delta_ids: deltaIds,
  });
  return response.data;
};

export const getReview = async (reviewId: string): Promise<SmReview> => {
  const response = await client.get<SmReview>(`/sm/reviews/${reviewId}`);
  return response.data;
};

/**
 * Feature #340 — settle one SKILL: every competing proposal on it resolves
 * to this agreed rating at once, and the profile rating is written once.
 *
 * Returns the whole review, not the group. Resolving changes the subject's
 * current rating, which every other group on screen shows as its baseline,
 * so a partial response would leave the page rendering a stale one.
 */
export const resolveSkill = async (
  reviewId: string,
  skillId: string,
  agreedRating: number,
  deltaNotes?: string
): Promise<SmReview> => {
  const response = await client.post<SmReview>(
    `/sm/reviews/${reviewId}/skills/${skillId}/resolve`,
    { agreed_rating: agreedRating, delta_notes: deltaNotes }
  );
  return response.data;
};

/**
 * @deprecated Feature #340 — resolving a lone delta is not a decision. The
 * endpoint still exists and now settles the delta's whole skill group, but
 * new code should call `resolveSkill`, which says what it does.
 */
export const resolveDelta = async (
  reviewId: string,
  deltaId: string,
  agreedRating: number,
  deltaNotes?: string
): Promise<SmDelta> => {
  const response = await client.post<SmDelta>(
    `/sm/reviews/${reviewId}/deltas/${deltaId}/resolve`,
    { agreed_rating: agreedRating, delta_notes: deltaNotes }
  );
  return response.data;
};

export const closeReview = async (
  reviewId: string,
  meetingNotes?: string
): Promise<{ review_id: string; deltas_detached: number }> => {
  const response = await client.post(`/sm/reviews/${reviewId}/close`, {
    meeting_notes: meetingNotes,
  });
  return response.data;
};

// --- Profiles & roster ---------------------------------------------------------

export interface SmProfileSkill {
  skill_id: string;
  skill_name: string;
  skill_type: SkillType;
  current_rating?: number | null;
  rating_label?: string | null;
  source_type?: string | null;
  source_id?: string | null;
  /**
   * Full provenance phrase, ending in its own date:
   * "Self assessment by Ben Cashman on 07 Jul 2026". Used as the row
   * tooltip — a review-sourced rating's resolution date is NOT the same
   * value as `last_assessed_at`, so this is the only place it survives.
   */
  source_label?: string | null;
  /**
   * Feature #309 — the same phrase with the date removed
   * ("Self assessment by Ben Cashman", "Agreed at review"), for the
   * strength-spine subtext which renders `last_assessed_at` alongside it.
   */
  source_actor_label?: string | null;
  last_assessed_at?: string | null;
  is_stale: boolean;
}

/**
 * Feature #320 — what one submitted rating actually did.
 *
 * Only `first_rated` moved anything. Under R3 a rating that differs from
 * the current one opens a delta and leaves the rating alone — for a *self*
 * assessment exactly as much as a peer one — so `delta_opened` must never
 * be rendered as a transition.
 */
export type SmHistoryOutcome = 'first_rated' | 'confirmed' | 'delta_opened';

export interface SmHistoryItem {
  skill_id: string;
  skill_name: string;
  rating: number;
  comment?: string | null;
  outcome: SmHistoryOutcome;
  /** Effective rating immediately before this submission; null when first rated. */
  previous_rating?: number | null;
  /** The delta this item raised, when `outcome === 'delta_opened'`. */
  delta_id?: string | null;
}

/**
 * Feature #347 — one skill an assessor declined to rate.
 *
 * The row existing at all is the point: "declined" and "absent" are
 * different facts, and without this the requester cannot tell whether the
 * assessor had no view or simply never reached the skill.
 */
export interface SmHistoryDecline {
  skill_id: string;
  skill_name: string;
  reason?: string | null;
}

export interface SmHistorySubmission {
  submission_id: string;
  assessment_type: 'self' | 'peer';
  assessor_user_id: string;
  assessor_display_name?: string | null;
  submitted_at: string;
  items: SmHistoryItem[];
  /** Computed server-side — a collapsed event costs one line and no diffing. */
  first_rated_count: number;
  confirmed_count: number;
  deltas_opened_count: number;
  /** Feature #347 — skills the assessor explicitly could not judge. */
  declined: SmHistoryDecline[];
  /** Feature #326 — this answered a request, so it was advisory: nothing
   *  it proposed moved a rating, not even on a skill that had none. */
  was_requested: boolean;
}

/**
 * #364 — what a review decided about one skill, attributed to the review
 * and to nobody's proposal. The subject's `deltas` are filtered to their
 * own proposals, so a skill the review settled may carry no delta in their
 * payload at all; the agreed rating still reaches them through this.
 */
export interface SmHistoryReviewOutcome {
  skill_id: string;
  skill_name?: string | null;
  previous_rating?: number | null;
  agreed_rating?: number | null;
  resolved: boolean;
  /** #372 — the reviewer's note against the agreed rating. */
  notes?: string | null;
}

export interface SmHistoryReview {
  review_id: string;
  opened_at: string;
  resolved_at?: string | null;
  status: string;
  /** #372 — who closed the review, or who opened it while in progress. */
  conducted_by_user_id?: string | null;
  conducted_by_display_name?: string | null;
  meeting_notes?: string | null;
  /** Every proposal the caller may see — all of them for an sm_manager,
   *  only their own for the subject (#364). */
  deltas: SmDelta[];
  /** One per skill the review touched, whoever proposed on it (#364). */
  outcomes: SmHistoryReviewOutcome[];
}

/**
 * Feature #320 — submissions and reviews interleaved into one chronology,
 * newest first. Merged server-side because the ordering the client renders
 * has to be the ordering the `previous_rating` values were computed against.
 */
export interface SmHistoryEvent {
  event_type: 'self' | 'peer' | 'review';
  occurred_at: string;
  submission?: SmHistorySubmission | null;
  review?: SmHistoryReview | null;
}

export interface SmPersonProfile {
  user_id: string;
  display_name: string;
  skills: SmProfileSkill[];
  submissions: SmHistorySubmission[];
  reviews: SmHistoryReview[];
  /** The same submissions and reviews, interleaved. Prefer this over the two above. */
  history: SmHistoryEvent[];
  open_deltas: SmDelta[];
  /**
   * Feature #313 — every active capability in this person's context, so
   * the badge block renders without a second request. Since #356 `met`
   * means a Skills Manager assigned the badge, not that ratings earned it.
   */
  capabilities: SmCapabilityListEntry[];
}

export interface SmStaffRosterEntry {
  user_id: string;
  display_name: string;
  email: string;
  skills_rated: number;
  open_deltas: number;
  stale_skills: number;
  last_assessed_at?: string | null;
}

export const getPersonProfile = async (userId: string): Promise<SmPersonProfile> => {
  const response = await client.get<SmPersonProfile>(`/sm/people/${userId}/profile`);
  return response.data;
};

export const getStaffRoster = async (): Promise<SmStaffRosterEntry[]> => {
  const response = await client.get<{ staff: SmStaffRosterEntry[] }>('/sm/staff');
  return response.data.staff;
};

// --- Coverage heatmap (Feature #359) -------------------------------------------
// snake_case, matching the backend (convention 2).

export interface SmCoveragePerson {
  user_id: string;
  display_name: string;
}

export interface SmCoverageCell {
  rating: number;
  count: number;
  people: SmCoveragePerson[];
}

export interface SmCoverageSkill {
  skill_id: string;
  name: string;
  rated_count: number;
  /** Sum of current ratings across people: 1×5 = 5, 3×1 + 1×5 = 8. */
  coverage: number;
  /** Always ratings 1–5, in order. */
  cells: SmCoverageCell[];
}

export interface SmCoverageLevel {
  value: number;
  label?: string | null;
}

export interface SmCoverage {
  levels: SmCoverageLevel[];
  skills: SmCoverageSkill[];
  /** Largest cell on the grid — top of the colour scale. */
  max_cell_count: number;
}

export const getSkillsCoverage = async (): Promise<SmCoverage> => {
  const response = await client.get<SmCoverage>('/sm/coverage');
  return response.data;
};

// --- Capabilities (Feature #313, reshaped by #356) ------------------------------

/**
 * A capability is a named thing The Lab can deliver. Since #356 its badge is
 * something a Skills Manager assigns to a person (and can take back), stored
 * as an assignment row — not something computed from ratings.
 *
 * The requirement set (`requirements`, `replaceCapabilityRequirements`) is
 * retained on the wire but inert: it awards nothing and the UI no longer
 * shows it.
 */

/** Retained for wire compatibility only — always empty since #356. */
export interface SmCapabilityShortfall {
  skill_id: string;
  skill_name: string;
  skill_type: SkillType;
  required: number;
  /** null = never assessed, deliberately distinct from "assessed below". */
  current?: number | null;
}

export interface SmCapabilityRequirement {
  skill_id: string;
  skill_name: string;
  skill_type: SkillType;
  min_rating: number;
  /**
   * False when the catalogue skill is deprecated — skipped in evaluation.
   * A requirement whose `skill_type` is not 'technical' is skipped too
   * (#355); the two are told apart client-side by `skill_type`.
   */
  skill_is_active: boolean;
}

export interface SmCapabilityBadge {
  capability_id: string;
  name: string;
  description?: string | null;
  /** Held: a Skills Manager assigned it, it is not revoked, and the capability is active. */
  met: boolean;
  /** The grant behind `met`. All null when not held. */
  assigned_at?: string | null;
  assigned_by_user_id?: string | null;
  assigned_by_label?: string | null;
  note?: string | null;
  /** Always `[]` since #356; kept so an older bundle keeps rendering. */
  shortfall: SmCapabilityShortfall[];
  /** Always `0` since #356; kept so an older bundle keeps rendering. */
  skipped_requirements: number;
}

export interface SmCapabilityListEntry extends SmCapabilityBadge {
  requirements: SmCapabilityRequirement[];
  is_active: boolean;
}

export interface SmCapabilityList {
  subject_user_id: string;
  subject_display_name: string;
  items: SmCapabilityListEntry[];
}

export interface SmCapability {
  id: string;
  name: string;
  description?: string | null;
  is_active: boolean;
  requirements: SmCapabilityRequirement[];
  created_at: string;
  updated_at: string;
}

export interface SmCapabilityRequirementInput {
  skill_id: string;
  min_rating: number;
}

/**
 * The catalogue evaluated in one person's context. Omit `userId` for
 * yourself; naming someone else requires sm_manager and is rejected at the
 * API, not merely hidden in the UI.
 */
export const getCapabilities = async (params?: {
  userId?: string;
  metOnly?: boolean;
  includeInactive?: boolean;
}): Promise<SmCapabilityList> => {
  const response = await client.get<SmCapabilityList>('/sm/capabilities', {
    params: {
      user_id: params?.userId,
      met_only: params?.metOnly,
      include_inactive: params?.includeInactive,
    },
  });
  return response.data;
};

export const createCapability = async (data: {
  name: string;
  description?: string;
  requirements?: SmCapabilityRequirementInput[];
}): Promise<SmCapability> => {
  const response = await client.post<SmCapability>('/sm/capabilities', data);
  return response.data;
};

export const updateCapability = async (
  capabilityId: string,
  data: { name?: string; description?: string; is_active?: boolean }
): Promise<SmCapability> => {
  const response = await client.patch<SmCapability>(
    `/sm/capabilities/${capabilityId}`,
    data
  );
  return response.data;
};

/**
 * Replaces the whole requirement set — send every minimum, not a delta.
 * Inert since #356: the endpoint still stores the set but nothing reads it
 * to decide who holds a badge, and no screen calls this any more.
 */
export const replaceCapabilityRequirements = async (
  capabilityId: string,
  requirements: SmCapabilityRequirementInput[]
): Promise<SmCapability> => {
  const response = await client.put<SmCapability>(
    `/sm/capabilities/${capabilityId}/skills`,
    { requirements }
  );
  return response.data;
};

/**
 * Give one person a capability badge (sm_manager, Feature #356). 409 when
 * already held or the capability is deprecated; 403 when naming yourself.
 */
export const assignCapability = async (
  capabilityId: string,
  data: { user_id: string; note?: string }
): Promise<SmCapabilityBadge> => {
  const response = await client.post<SmCapabilityBadge>(
    `/sm/capabilities/${capabilityId}/assignments`,
    data
  );
  return response.data;
};

/** Take a badge back (sm_manager). Soft — the assignment is marked revoked, not deleted. */
export const revokeCapability = async (
  capabilityId: string,
  userId: string
): Promise<void> => {
  await client.delete(`/sm/capabilities/${capabilityId}/assignments/${userId}`);
};

// --- Settings ------------------------------------------------------------------

export interface SmSetting {
  id: string;
  key: string;
  value: string;
  description?: string | null;
  updated_by?: string | null;
  updated_at: string;
}

export const getSmSettings = async (): Promise<SmSetting[]> => {
  const response = await client.get<SmSetting[]>('/sm/settings');
  return response.data;
};

export const updateSmSetting = async (
  key: string,
  value: string,
  description?: string
): Promise<SmSetting> => {
  const response = await client.patch<SmSetting>(`/sm/settings/${key}`, {
    value,
    description,
  });
  return response.data;
};

// --- Skill requests (Feature #374) -------------------------------------------
// A user's ask, from inside an assessment, for a technical skill the
// catalogue lacks. Nothing reaches the catalogue until an sm_admin accepts.

export type SmSkillRequestStatus = 'in_review' | 'accepted' | 'rejected';

export interface SmSkillRequest {
  id: string;
  name: string;
  justification: string;
  requester_user_id: string;
  requester_display_name?: string | null;
  /** Whose assessment the request was lodged from — context, not permission. */
  subject_user_id?: string | null;
  subject_display_name?: string | null;
  status: SmSkillRequestStatus;
  resolved_by?: string | null;
  resolved_by_display_name?: string | null;
  resolved_at?: string | null;
  resolution_note?: string | null;
  /** Set on accept: the catalogue skill the request produced (or matched). */
  created_skill_id?: string | null;
  created_skill_name?: string | null;
  created_at: string;
  updated_at: string;
}

export interface SmSkillRequestPage {
  items: SmSkillRequest[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export const createSkillRequest = async (data: {
  name: string;
  justification: string;
  subject_user_id?: string | null;
}): Promise<SmSkillRequest> => {
  const response = await client.post<SmSkillRequest>('/sm/skill-requests', data);
  return response.data;
};

/** The caller's own requests, newest first; optionally only those lodged
 *  from one person's assessment. */
export const getMySkillRequests = async (
  subjectUserId?: string
): Promise<SmSkillRequest[]> => {
  const response = await client.get<SmSkillRequest[]>('/sm/skill-requests/mine', {
    params: subjectUserId ? { subject_user_id: subjectUserId } : undefined,
  });
  return response.data;
};

/** The review queue (sm_admin). */
export const getSkillRequests = async (params: {
  status?: SmSkillRequestStatus;
  page?: number;
  page_size?: number;
}): Promise<SmSkillRequestPage> => {
  const response = await client.get<SmSkillRequestPage>('/sm/skill-requests', {
    params: {
      status: params.status,
      page: params.page ?? 1,
      page_size: params.page_size ?? 20,
    },
  });
  return response.data;
};

export const acceptSkillRequest = async (
  requestId: string,
  data: { name?: string; description?: string; note?: string }
): Promise<SmSkillRequest> => {
  const response = await client.post<SmSkillRequest>(
    `/sm/skill-requests/${requestId}/accept`,
    data
  );
  return response.data;
};

export const rejectSkillRequest = async (
  requestId: string,
  data: { note?: string }
): Promise<SmSkillRequest> => {
  const response = await client.post<SmSkillRequest>(
    `/sm/skill-requests/${requestId}/reject`,
    data
  );
  return response.data;
};
